import React from 'react';
import HelpTooltip from '../../../../shared/components/HelpTooltip';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
  ReferenceDot,
  Label
} from 'recharts';
import { AlertTriangle, TrendingUp, Target, DollarSign } from 'lucide-react';

interface Big6UnifiedParams {
  povertyLine?: {
    povertyLinePercent: number;
    optimalMinPercent: number;
    optimalMaxPercent: number;
    adjustments?: {
      industryAdjustment: number;
      gapAdjustment: number;
      industry: string;
      weightedAverageGap: number;
    };
  };
  gapAnalysis?: {
    weightedAverageGap: number;
    topRiskCategories: string[];
  };
  budgetEvaluation?: {
    budgetStatus: string;
    budgetGap: number;
  };
}

interface CyberPovertyLineChartProps {
  annualRevenue: number;
  currentInvestment: number;
  currentReadiness: number;
  industryBenchmark?: {
    averageReadiness?: number;
    recommendedSecurityPercent?: number;
  };
  povertyLinePercent?: number; // Default 0.3% of revenue
  optimalMinPercent?: number;  // Default 0.8% of revenue
  optimalMaxPercent?: number;  // Default 1.8% of revenue
  big6UnifiedParams?: Big6UnifiedParams; // Dynamic thresholds from Big 6 gap analysis
}

// Generate S-curve data points for the investment vs readiness relationship
const generateCurveData = (
  annualRevenue: number,
  povertyLinePercent: number,
  optimalMinPercent: number,
  optimalMaxPercent: number
) => {
  const points = [];
  const maxInvestmentPercent = 3.0; // Up to 3% of revenue
  const steps = 50;

  for (let i = 0; i <= steps; i++) {
    const investmentPercent = (i / steps) * maxInvestmentPercent;
    const investment = annualRevenue * (investmentPercent / 100);

    // S-curve formula: readiness grows slowly below poverty line,
    // accelerates in optimal zone, then plateaus
    let readiness: number;

    if (investmentPercent <= povertyLinePercent) {
      // Below poverty line: minimal protection, steep risk
      readiness = (investmentPercent / povertyLinePercent) * 25;
    } else if (investmentPercent <= optimalMinPercent) {
      // Approaching optimal: moderate growth
      const progress = (investmentPercent - povertyLinePercent) / (optimalMinPercent - povertyLinePercent);
      readiness = 25 + progress * 20;
    } else if (investmentPercent <= optimalMaxPercent) {
      // Optimal zone: best ROI, steepest growth
      const progress = (investmentPercent - optimalMinPercent) / (optimalMaxPercent - optimalMinPercent);
      readiness = 45 + progress * 30;
    } else {
      // Over-investment: diminishing returns
      const overInvestment = investmentPercent - optimalMaxPercent;
      const diminishingFactor = 1 - Math.exp(-overInvestment * 2);
      readiness = 75 + diminishingFactor * 15; // Asymptote around 90%
    }

    // Determine which zone this point falls into
    let zone: string;
    if (investmentPercent < povertyLinePercent) {
      zone = 'poverty';
    } else if (investmentPercent < optimalMinPercent) {
      zone = 'underinvested';
    } else if (investmentPercent <= optimalMaxPercent) {
      zone = 'optimal';
    } else {
      zone = 'overinvested';
    }

    points.push({
      investment,
      investmentPercent,
      readiness: Math.round(readiness * 10) / 10,
      zone,
      investmentFormatted: formatCurrency(investment),
      revenueAtRisk: annualRevenue * ((100 - readiness) / 100) * 0.15 // ~15% of unprotected revenue at risk
    });
  }

  return points;
};

const formatCurrency = (amount: number): string => {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  return `$${(amount / 1000).toFixed(0)}K`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const zoneColors: Record<string, { bg: string; text: string; label: string }> = {
      poverty: { bg: 'bg-red-100', text: 'text-[#ccd6f6]', label: 'Below Cyber Poverty Line' },
      underinvested: { bg: 'bg-orange-100', text: 'text-[#ccd6f6]', label: 'Under-Invested' },
      optimal: { bg: 'bg-green-100', text: 'text-[#ccd6f6]', label: 'Optimal Zone' },
      overinvested: { bg: 'bg-yellow-100', text: 'text-[#ccd6f6]', label: 'Diminishing Returns' }
    };
    const zone = zoneColors[data.zone] || zoneColors.underinvested;

    return (
      <div className="ra-glass p-4 rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.12)]">
        <div className={`inline-block px-2 py-1 rounded text-xs font-medium mb-2 ${zone.bg} ${zone.text}`}>
          {zone.label}
        </div>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-[#a8b2d1]">Investment:</span>
            <span className="font-semibold">{data.investmentFormatted}</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-[#a8b2d1]">% of Revenue:</span>
            <span className="font-semibold">{data.investmentPercent.toFixed(2)}%</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-[#a8b2d1]">Readiness:</span>
            <span className="font-semibold">{data.readiness}%</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-[#a8b2d1]">Revenue at Risk:</span>
            <span className="font-semibold text-red-600">{formatCurrency(data.revenueAtRisk)}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const CyberPovertyLineChart: React.FC<CyberPovertyLineChartProps> = ({
  annualRevenue,
  currentInvestment,
  currentReadiness,
  industryBenchmark,
  povertyLinePercent: propPovertyLinePercent = 0.3,
  optimalMinPercent: propOptimalMinPercent = 0.8,
  optimalMaxPercent: propOptimalMaxPercent = 1.8,
  big6UnifiedParams
}) => {
  // Use Big 6 dynamic thresholds if available, otherwise fall back to props/defaults
  const isDynamic = !!big6UnifiedParams?.povertyLine;
  const povertyLinePercent = big6UnifiedParams?.povertyLine?.povertyLinePercent ?? propPovertyLinePercent;
  const optimalMinPercent = big6UnifiedParams?.povertyLine?.optimalMinPercent ?? propOptimalMinPercent;
  const optimalMaxPercent = big6UnifiedParams?.povertyLine?.optimalMaxPercent ?? propOptimalMaxPercent;

  const curveData = generateCurveData(annualRevenue, povertyLinePercent, optimalMinPercent, optimalMaxPercent);

  const povertyLineInvestment = annualRevenue * (povertyLinePercent / 100);
  const optimalMinInvestment = annualRevenue * (optimalMinPercent / 100);
  const optimalMaxInvestment = annualRevenue * (optimalMaxPercent / 100);

  const currentInvestmentPercent = (currentInvestment / annualRevenue) * 100;

  // Determine current status
  const isBelowPovertyLine = currentInvestmentPercent < povertyLinePercent;
  const isInOptimalZone = currentInvestmentPercent >= optimalMinPercent && currentInvestmentPercent <= optimalMaxPercent;
  const isOverInvested = currentInvestmentPercent > optimalMaxPercent;

  // Find the closest data point to current investment for the marker
  const currentPoint = curveData.reduce((prev, curr) =>
    Math.abs(curr.investment - currentInvestment) < Math.abs(prev.investment - currentInvestment) ? curr : prev
  );

  return (
    <div className="ra-glass rounded-lg shadow-[0_4px_24px_rgba(0,0,0,0.3)] p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-[#e6f1ff] flex items-center gap-1.5">Security Investment Curve <HelpTooltip contentKey="compass.povertyLine.chart" /></h2>
          <p className="text-sm text-[#a8b2d1] mt-1">
            Cyber Poverty Line & Optimal Investment Zones
            {isDynamic && (
              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-[#ccd6f6]">
                Big 6 Gap-Adjusted
              </span>
            )}
          </p>
          {isDynamic && big6UnifiedParams?.povertyLine?.adjustments && (
            <p className="text-xs text-[#8892b0] mt-1">
              Thresholds adjusted for {big6UnifiedParams.povertyLine.adjustments.industry} industry
              (+{(big6UnifiedParams.povertyLine.adjustments.industryAdjustment * 100).toFixed(1)}%)
              and {big6UnifiedParams.povertyLine.adjustments.weightedAverageGap.toFixed(1)} weighted gap
              (+{(big6UnifiedParams.povertyLine.adjustments.gapAdjustment * 100).toFixed(1)}%)
            </p>
          )}
        </div>
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded"></div>
            <span>Poverty Zone</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-orange-400 rounded"></div>
            <span>Under-invested</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded"></div>
            <span>Optimal</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-yellow-400 rounded"></div>
            <span>Diminishing Returns</span>
          </div>
        </div>
      </div>

      {/* Alert Banner if below poverty line */}
      {isBelowPovertyLine && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle aria-hidden="true" className="h-5 w-5 text-red-600 mt-0.5" />
            <div>
              <h4 className="font-semibold text-[#ccd6f6]">Cyber Poverty Alert</h4>
              <p className="text-sm text-red-700 mt-1">
                Your organization is below the Cyber Poverty Line. At {formatCurrency(currentInvestment)} ({currentInvestmentPercent.toFixed(2)}% of revenue),
                security controls are largely ineffective. Minimum viable security requires {formatCurrency(povertyLineInvestment)} ({povertyLinePercent.toFixed(2)}% of revenue).
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Chart */}
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={curveData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <defs>
              {/* Gradient for the area under the curve */}
              <linearGradient id="readinessGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#ef4444" stopOpacity={0.6} />
                <stop offset={`${(povertyLinePercent / 3) * 100}%`} stopColor="#ef4444" stopOpacity={0.6} />
                <stop offset={`${(povertyLinePercent / 3) * 100}%`} stopColor="#f97316" stopOpacity={0.5} />
                <stop offset={`${(optimalMinPercent / 3) * 100}%`} stopColor="#f97316" stopOpacity={0.5} />
                <stop offset={`${(optimalMinPercent / 3) * 100}%`} stopColor="#22c55e" stopOpacity={0.5} />
                <stop offset={`${(optimalMaxPercent / 3) * 100}%`} stopColor="#22c55e" stopOpacity={0.5} />
                <stop offset={`${(optimalMaxPercent / 3) * 100}%`} stopColor="#eab308" stopOpacity={0.4} />
                <stop offset="100%" stopColor="#eab308" stopOpacity={0.4} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />

            <XAxis
              dataKey="investment"
              tickFormatter={(value) => formatCurrency(value)}
              tick={{ fontSize: 11 }}
              stroke="#6b7280"
            >
              <Label value="Security Investment" position="bottom" offset={0} style={{ fontSize: 12, fill: '#374151' }} />
            </XAxis>

            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 11 }}
              stroke="#6b7280"
              tickFormatter={(value) => `${value}%`}
            >
              <Label value="Security Readiness" angle={-90} position="left" offset={0} style={{ fontSize: 12, fill: '#374151' }} />
            </YAxis>

            <Tooltip content={<CustomTooltip />} />

            {/* Cyber Poverty Line - vertical reference */}
            <ReferenceLine
              x={povertyLineInvestment}
              stroke="#dc2626"
              strokeWidth={2}
              strokeDasharray="5 5"
            >
              <Label
                value="Cyber Poverty Line"
                position="top"
                fill="#dc2626"
                fontSize={11}
                fontWeight="bold"
              />
            </ReferenceLine>

            {/* Optimal Zone Start */}
            <ReferenceLine
              x={optimalMinInvestment}
              stroke="#16a34a"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            >
              <Label
                value="Optimal Start"
                position="insideTopRight"
                fill="#16a34a"
                fontSize={10}
              />
            </ReferenceLine>

            {/* Optimal Zone End */}
            <ReferenceLine
              x={optimalMaxInvestment}
              stroke="#ca8a04"
              strokeWidth={1.5}
              strokeDasharray="3 3"
            >
              <Label
                value="Diminishing Returns"
                position="insideTopLeft"
                fill="#ca8a04"
                fontSize={10}
              />
            </ReferenceLine>

            {/* The S-curve area */}
            <Area
              type="monotone"
              dataKey="readiness"
              stroke="#3b82f6"
              strokeWidth={3}
              fill="url(#readinessGradient)"
            />

            {/* Current position marker */}
            <ReferenceDot
              x={currentPoint.investment}
              y={currentReadiness}
              r={8}
              fill={isBelowPovertyLine ? "#dc2626" : isInOptimalZone ? "#16a34a" : isOverInvested ? "#ca8a04" : "#f97316"}
              stroke="#ffffff"
              strokeWidth={3}
            />

            {/* Current position vertical line */}
            <ReferenceLine
              x={currentInvestment}
              stroke={isBelowPovertyLine ? "#dc2626" : isInOptimalZone ? "#16a34a" : isOverInvested ? "#ca8a04" : "#f97316"}
              strokeWidth={2}
            >
              <Label
                value={`You: ${formatCurrency(currentInvestment)}`}
                position="insideBottomRight"
                fill={isBelowPovertyLine ? "#dc2626" : isInOptimalZone ? "#16a34a" : "#f97316"}
                fontSize={11}
                fontWeight="bold"
              />
            </ReferenceLine>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mt-6">
        <div className={`p-4 rounded-lg border-2 ${isBelowPovertyLine ? 'border-red-500 bg-red-50' : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]'}`}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle aria-hidden="true" className={`h-4 w-4 ${isBelowPovertyLine ? 'text-red-600' : 'text-[#5a6a8a]'}`} />
            <span className="text-xs font-medium text-[#a8b2d1]">Poverty Line</span>
          </div>
          <div className="text-lg font-bold text-[#e6f1ff]">{formatCurrency(povertyLineInvestment)}</div>
          <div className="text-xs text-[#8892b0]">{povertyLinePercent.toFixed(2)}% of revenue</div>
        </div>

        <div className={`p-4 rounded-lg border-2 ${!isBelowPovertyLine && !isInOptimalZone && !isOverInvested ? 'border-orange-500 bg-orange-50' : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]'}`}>
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp aria-hidden="true" className={`h-4 w-4 ${!isBelowPovertyLine && !isInOptimalZone && !isOverInvested ? 'text-orange-600' : 'text-[#5a6a8a]'}`} />
            <span className="text-xs font-medium text-[#a8b2d1]">Optimal Start</span>
          </div>
          <div className="text-lg font-bold text-[#e6f1ff]">{formatCurrency(optimalMinInvestment)}</div>
          <div className="text-xs text-[#8892b0]">{optimalMinPercent.toFixed(2)}% of revenue</div>
        </div>

        <div className={`p-4 rounded-lg border-2 ${isInOptimalZone ? 'border-green-500 bg-green-50' : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Target aria-hidden="true" className={`h-4 w-4 ${isInOptimalZone ? 'text-green-600' : 'text-[#5a6a8a]'}`} />
            <span className="text-xs font-medium text-[#a8b2d1]">Optimal End</span>
          </div>
          <div className="text-lg font-bold text-[#e6f1ff]">{formatCurrency(optimalMaxInvestment)}</div>
          <div className="text-xs text-[#8892b0]">{optimalMaxPercent.toFixed(2)}% of revenue</div>
        </div>

        <div className={`p-4 rounded-lg border-2 ${isOverInvested ? 'border-yellow-500 bg-yellow-50' : 'border-[rgba(255,255,255,0.12)] bg-[rgba(255,255,255,0.03)]'}`}>
          <div className="flex items-center gap-2 mb-2">
            <DollarSign aria-hidden="true" className={`h-4 w-4 ${isOverInvested ? 'text-yellow-600' : 'text-[#5a6a8a]'}`} />
            <span className="text-xs font-medium text-[#a8b2d1]">Your Investment</span>
          </div>
          <div className="text-lg font-bold text-[#e6f1ff]">{formatCurrency(currentInvestment)}</div>
          <div className="text-xs text-[#8892b0]">{currentInvestmentPercent.toFixed(2)}% of revenue</div>
        </div>
      </div>

      {/* Status Message */}
      <div className={`mt-4 p-4 rounded-lg ${
        isBelowPovertyLine ? 'bg-red-100 border border-red-300' :
        isInOptimalZone ? 'bg-green-100 border border-green-300' :
        isOverInvested ? 'bg-yellow-100 border border-yellow-300' :
        'bg-orange-100 border border-orange-300'
      }`}>
        <p className={`text-sm font-medium ${
          isBelowPovertyLine ? 'text-[#ccd6f6]' :
          isInOptimalZone ? 'text-[#ccd6f6]' :
          isOverInvested ? 'text-[#ccd6f6]' :
          'text-[#ccd6f6]'
        }`}>
          {isBelowPovertyLine && (
            <>
              <strong>Critical:</strong> You are {formatCurrency(povertyLineInvestment - currentInvestment)} below the Cyber Poverty Line.
              At this level, your security posture provides minimal protection against modern threats.
              Immediate investment increase to at least {formatCurrency(povertyLineInvestment)} is strongly recommended.
            </>
          )}
          {!isBelowPovertyLine && !isInOptimalZone && !isOverInvested && (
            <>
              <strong>Under-invested:</strong> You are above the poverty line but {formatCurrency(optimalMinInvestment - currentInvestment)} short of the optimal zone.
              Consider increasing investment to {formatCurrency(optimalMinInvestment)} to maximize your security ROI.
            </>
          )}
          {isInOptimalZone && (
            <>
              <strong>Optimal:</strong> Your investment is in the sweet spot where each dollar provides maximum risk reduction.
              Maintain current spending levels and focus on optimization of existing controls.
            </>
          )}
          {isOverInvested && (
            <>
              <strong>Diminishing Returns:</strong> You are investing {formatCurrency(currentInvestment - optimalMaxInvestment)} beyond the optimal zone.
              Consider reallocating excess funds to other business initiatives or advanced security capabilities.
            </>
          )}
        </p>
      </div>
    </div>
  );
};

export default CyberPovertyLineChart;
